import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

export default function HowItWorks() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  usePageSeo({
    title: 'Как это работает | Promokod.online',
    description: 'Пошаговая инструкция по использованию промокодов на Promokod.online: как найти предложение, перейти по ссылке, скопировать код и получить скидку.',
    canonicalPath: '/how-it-works',
  });

  const steps = [
    {
      number: 1,
      icon: 'ri-search-line',
      title: 'Найдите нужный магазин',
      description: 'Воспользуйтесь поиском или выберите магазин из каталога. У нас более 500 популярных интернет-магазинов с актуальными промокодами.'
    },
    {
      number: 2,
      icon: 'ri-ticket-line',
      title: 'Выберите промокод',
      description: 'Просмотрите доступные промокоды и акции. Каждый код содержит подробное описание скидки и условия применения.'
    },
    {
      number: 3,
      icon: 'ri-file-copy-line',
      title: 'Скопируйте код',
      description: 'Нажмите кнопку «Скопировать код» — промокод автоматически сохранится в буфер обмена, а вы перейдёте на сайт магазина.'
    },
    {
      number: 4,
      icon: 'ri-shopping-cart-line',
      title: 'Примените при оформлении',
      description: 'Вставьте скопированный промокод в специальное поле при оформлении заказа и получите скидку. Экономьте на каждой покупке!'
    }
  ];

  const tips = [
    {
      icon: 'ri-time-line',
      title: 'Проверяйте срок действия',
      description: 'Обращайте внимание на дату окончания акции — используйте промокоды до истечения срока.'
    },
    {
      icon: 'ri-stack-line',
      title: 'Пробуйте несколько кодов',
      description: 'Если один промокод не сработал, попробуйте другой — у нас всегда есть альтернативные варианты.'
    },
    {
      icon: 'ri-notification-line',
      title: 'Следите за обновлениями',
      description: 'Мы ежедневно добавляем новые промокоды и акции. Заходите регулярно, чтобы не пропустить выгодные предложения.'
    },
    {
      icon: 'ri-bookmark-line',
      title: 'Читайте условия',
      description: 'Некоторые промокоды имеют ограничения: минимальная сумма заказа, категория товаров или только для новых пользователей.'
    }
  ];

  const faqs = [
    {
      question: 'Почему промокод не работает?',
      answer: 'Причин может быть несколько: истёк срок действия, не выполнены условия применения (минимальная сумма заказа, категория товаров), промокод предназначен только для новых пользователей, или код уже был использован ранее. Попробуйте другой промокод из нашего списка.'
    },
    {
      question: 'Можно ли использовать несколько промокодов одновременно?',
      answer: 'Обычно интернет-магазины позволяют применить только один промокод на заказ. Однако некоторые магазины разрешают комбинировать промокоды с другими акциями и распродажами. Проверяйте условия в описании каждого промокода.'
    },
    {
      question: 'Как часто обновляются промокоды?',
      answer: 'Мы обновляем базу промокодов ежедневно. Наша команда постоянно мониторит новые акции и предложения от магазинов-партнёров, чтобы вы всегда имели доступ к актуальным и работающим кодам.'
    },
    {
      question: 'Нужна ли регистрация для использования промокодов?',
      answer: 'Нет, регистрация на нашем сайте не требуется. Все промокоды доступны бесплатно и без ограничений. Просто выберите нужный код, скопируйте его и используйте при оформлении заказа в магазине.'
    },
    {
      question: 'Что делать, если промокод не скопировался?',
      answer: 'Если автоматическое копирование не сработало, вы можете выделить код вручную и скопировать его с помощью Ctrl+C (Cmd+C на Mac). Также убедитесь, что в вашем браузере разрешён доступ к буферу обмена.'
    },
    {
      question: 'Можно ли использовать промокоды в мобильном приложении магазина?',
      answer: 'Да, большинство промокодов работают как на сайте, так и в мобильном приложении магазина. Просто скопируйте код и вставьте его в соответствующее поле при оформлении заказа в приложении.'
    }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-50 to-blue-50 py-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Как это работает?
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Экономить с Promokod.online очень просто! Следуйте нашей пошаговой инструкции 
              и получайте скидки на покупки в любимых интернет-магазинах.
            </p>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {steps.map((step) => (
                <div 
                  key={step.number}
                  className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                        <i className={`${step.icon} text-white text-2xl`}></i>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Советы по использованию промокодов
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Следуйте этим рекомендациям, чтобы получить максимальную выгоду от использования промокодов
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tips.map((tip, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:border-orange-500 transition-colors"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${tip.icon} text-orange-600 text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {tip.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Часто задаваемые вопросы
              </h2>
              <p className="text-gray-600">
                Ответы на популярные вопросы о работе с промокодами
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-blue-600 transition-colors"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-blue-600 text-xl`}></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Готовы начать экономить?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Тысячи актуальных промокодов ждут вас. Начните экономить прямо сейчас!
            </p>
            <a 
              href="/"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Перейти к промокодам
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
